type Hero = {
  title: string;
  description: string;
  badge: string;
  assessTitle: string;
  checklist: Array<string>;
  checkIconUrl: string;
  formTitle: string;
};

type Card = {
  title: string;
  text: string;
  iconUrl?: string;
  order?: number;
  linkUrl?: string;
};

type CardWithIcon = {
  title: string;
  text: string;
  iconUrl: string;
};

type CardsSection = {
  title: string;
  text?: string;
  cards: Array<Card>;
};

type CardsSectionWithText = {
  title: string;
  text: string;
  cards: Array<CardWithIcon>;
};

type CtaBanner = {
  title: string;
  description: string;
  btnText: string;
  imageUrl: string;
};

type ProjectItem = {
  title: string;
  text: string;
  iconSrc: string;
  link: string;
};

type ProjectsShowcase = {
  title: string;
  description: string;
  link: string;
  items: Array<ProjectItem>;
};

type TechDueDiligenceState = {
  hero: Hero;
  financialDDMisses: CardsSection;
  audience: CardsSectionWithText;
  checklist: CardsSection;
  ctaBanner: CtaBanner;
  projectsShowcase: ProjectsShowcase;
};

export const HERO_FORM_ID = 'tech-due-diligence-form';

function imagePathBuilder({
  name,
  ext = 'png',
  isShared = false,
}: {
  name: string;
  ext?: string;
  isShared?: boolean;
}): string {
  const basePath = isShared ? '/shared' : '/tech-due-diligence';

  return `${basePath}/${name}.${ext}`;
}

export const techDueDiligenceState: TechDueDiligenceState = {
  hero: {
    title: 'Tech Due Diligence Checklist for Investors',
    description: 'Know what to look for in a software company before you write the cheque.',
    badge: '~20 min to complete',
    assessTitle: "What you'll assess",
    checklist: [
      'Delivery process & engineering maturity',
      'Codebase health & security posture',
      'AI tool adoption & team capability',
      'Architecture scalability & technical risk',
      'Key-person dependency & knowledge management',
      'Post-investment red flags',
    ],
    checkIconUrl: imagePathBuilder({ name: 'check-icon', ext: 'svg' }),
    formTitle: 'Download the checklist',
  },
  financialDDMisses: {
    title: 'What financial due diligence misses',
    cards: [
      {
        title: 'Financial DD is thorough',
        text: 'Financial DD is rigorous. Technical due diligence should verify the codebase, architecture, documentation, and development process, not conversations.',
        iconUrl: imagePathBuilder({ name: 'financial' }),
      },
      {
        title: 'Common investment surprise',
        text: "The biggest surprise after investing isn't financial. It's a codebase that only the founder understands and no one else can confidently maintain or scale.",
        iconUrl: imagePathBuilder({ name: 'investment' }),
      },
      {
        title: 'Risks you can catch early',
        text: 'Key person dependency, undocumented architecture, and missing CI/CD can all be identified before closing the deal if you know what to look for.',
        iconUrl: imagePathBuilder({ name: 'risks' }),
      },
    ],
  },

  audience: {
    title: 'For tech & non-tech people making technical decisions',
    text: 'Modern infrastructure built for security, compliance, and informed decisions.',
    cards: [
      {
        title: 'VC analysts and partners',
        iconUrl: imagePathBuilder({ name: 'analysts', isShared: false }),
        text: 'Evaluating target companies pre-investment through technical due diligence review.',
      },
      {
        title: 'PE portfolio managers',
        iconUrl: imagePathBuilder({ name: 'managers', isShared: false }),
        text: 'Assessing engineering risk during acquisition to uncover technical debt and scaling issues.',
      },
      {
        title: 'M&A advisors',
        iconUrl: imagePathBuilder({ name: 'advisors', isShared: false }),
        text: 'Running the technical component of a deal from due diligence through post-close integration.',
      },
      {
        title: 'Operators',
        iconUrl: imagePathBuilder({ name: 'operators', isShared: false }),
        text: 'Joining a portfolio company as CTO or interim technical lead to stabilize and scale engineering.',
      },
    ],
  },
  checklist: {
    title: '20 minutes. One conversation. Six areas.',
    text: 'Download the checklist and complete a 20-60 minute technical review.',
    cards: [
      {
        order: 1,
        title: 'Download your full review checklist',
        text: 'Get the free checklist instantly using the form above and start your review.',
      },
      {
        order: 2,
        title: 'Complete the 60-minute technical review',
        text: 'Go through it directly with the founder or CTO, or review it independently yourself.',
      },
      {
        order: 3,
        title: 'Score every completed review section',
        text: 'Use the built-in Level 1-4 framework to compare companies across deals.',
      },
      {
        order: 4,
        title: 'Get an independent technical opinion',
        text: 'Agilie provides independent technical assessments at any deal stage.',
      },
    ],
  },
  ctaBanner: {
    title: 'Have a deal on the table right now?',
    description:
      "Skip the form – book a call directly. We'll walk through the checklist together and give you a read within 48 hours.",
    btnText: 'Book a call',
    imageUrl: imagePathBuilder({ name: 'what-to-look', isShared: false }),
  },
  projectsShowcase: {
    title: "Agilie's work behind the checklist",
    description:
      'Built from 16 years auditing 200+ products across FinTech, HealthTech, and beyond, every checklist item maps to a real pattern behind post-investment failure.',
    link: '/success-stories',
    items: [
      {
        title: 'Chameleon Pay',
        text: 'Your handy multi-digital wallet',
        iconSrc: imagePathBuilder({ name: 'chameleon-pay' }),
        link: '/success-stories/chameleon-pay',
      },
      {
        title: 'Web.3 Neobanking',
        text: 'A neobanking crypto/fiat platform',
        iconSrc: imagePathBuilder({ name: 'wallex' }),
        link: '/success-stories/web-neobanking',
      },
      {
        title: 'Givingli',
        text: 'Greeting card app',
        iconSrc: imagePathBuilder({ name: 'givingli' }),
        link: '/success-stories/givingli',
      },
    ],
  },
};
